import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { range } from 'ramda';
import { EDStickyTable } from '../../StickyTable/EDStickyTable';
import { EDStickyTableHeader } from '../../StickyTable/Components/EDStickyTableHeader';
import { EDStickyTableHeaderStickyCell } from '../../StickyTable/Components/EDStickyTableHeaderStickyCell';
import { EDStickyTableHeaderCell } from '../../StickyTable/Components/EDStickyTableHeaderCell';
import { EDStickyTableRows } from '../../StickyTable/Components/EDStickyTableRows';
import { EDStickyTableRow } from '../../StickyTable/Components/EDStickyTableRow';
import { EDStickyTableStickyCell } from '../../StickyTable/Components/EDStickyTableStickyCell';
import {
  EDStickyTableCell,
  EDStickyTableCellSelectedEvent,
  EDStickyTableCellSelectionClickEvent,
} from '../../StickyTable/Components/EDStickyTableCell';
import { incDay, getDate, incMin, incHour } from '../../../Utils/dateUtils';
import {
  ICalendarViewProps,
  timeRangeOverlap,
  updateSelection,
  IBooking,
  prepareViewBookings,
} from '../Utils/EDCalendarViewUtils';
import { EDCalendarViewTimeViewBooking } from './EDCalendarViewTimeViewBooking';
import { EDCalendarViewWeekNumberBox } from './EDCalendarViewWeekNumberBox';
import { EDCalendarViewResourceNameCell } from './EDCalendarViewResourceNameCell';
import { EDCalendarViewClockCell } from './EDCalendarViewClockCell';
import { useIsSmallScreen } from '../../../Hooks/useFullscreenView';

export const EDCalendarViewTimeResourcesView: React.FC<
  ICalendarViewProps
> = props => {
  const isSmallScreen = useIsSmallScreen();

  const VISIBLE_TIME_ROWS = isSmallScreen ? 20 : 34; // Synliga halvtimmar

  const MIN_TIME_HEIGHT = 25;
  const HEAD_HEIGHT = 60;
  const TIME_WIDTH = 60;
  const TIME_ROWS = 48; // Ett dygn i halvtimmar
  const MIN_RES_WIDTH = 150;
  const WORK_HOUR_START = 8;
  const WORK_HOUR_END = 24;

  const [isDragging, setIsDragging] = useState(false);
  const resCount = props.resources.length;
  const viewStartTime = useMemo(
    () => getDate(props.currentDate),
    [props.currentDate]
  );
  const viewEndTime = useMemo(() => incDay(viewStartTime, 1), [viewStartTime]);

  const getHourHeight = (isSmallScreen: boolean): number => {
    if (isSmallScreen) {
      return Math.floor((props.height - HEAD_HEIGHT) / VISIBLE_TIME_ROWS);
    }
    return Math.max(
      MIN_TIME_HEIGHT,
      Math.floor((props.height - HEAD_HEIGHT) / VISIBLE_TIME_ROWS)
    );
  };

  const hourHeight = getHourHeight(isSmallScreen);
  const resWidth = Math.floor((props.width - TIME_WIDTH) / resCount);
  const scrollAreaHeight = hourHeight * TIME_ROWS;
  const initialScroll = WORK_HOUR_START * 2 - 1;

  // Hantera bokningar
  const bookingColumns = useMemo(
    () =>
      prepareViewBookings(
        props.bookings,
        viewStartTime,
        viewEndTime,
        booking => booking.resourceId,
        true
      ),
    [props.bookings, viewStartTime, viewEndTime]
  );

  // Hantera markering
  const selStart = useMemo(() => props.selStart, [props.selStart]);
  const selEnd = useMemo(() => props.selEnd, [props.selEnd]);
  const selResources = useMemo(() => props.selResources, [props.selResources]);
  const allResIds = useMemo(
    () => props.resources.map(res => res.id),
    [props.resources]
  );

  const onSelectionChange = props.onSelectionChange;
  const onSelectionClick = props.onSelectionClick;
  const onBookingClick = props.onBookingClick;

  const handleCellSelected: EDStickyTableCellSelectedEvent = useCallback(
    (
      res: number,
      halfHour: number,
      shift: boolean,
      ctrl: boolean,
      drag: boolean
    ) => {
      const cellStartTime = incMin(viewStartTime, halfHour * 30);
      const resId = allResIds[res];
      setIsDragging(drag);
      if (onSelectionChange) {
        const resSelected = (selResources ?? []).some(selId => selId === resId);
        let newResIds = [...(selResources ?? [])];
        if (ctrl) {
          if (!resSelected) {
            newResIds.push(resId);
          }
        } else if (
          (shift || drag) &&
          selResources &&
          selResources.length > 0 &&
          allResIds.length > 0
        ) {
          const idxFr = allResIds.indexOf(selResources[0]);
          const idxTo = allResIds.indexOf(resId);
          newResIds =
            idxFr === idxTo
              ? [resId]
              : [
                  selResources[0],
                  ...allResIds.slice(
                    Math.min(idxFr, idxTo) + 1,
                    Math.max(idxFr, idxTo)
                  ),
                  resId,
                ];
        } else {
          newResIds = [resId];
        }

        if ((shift || drag) && selStart && selEnd) {
          updateSelection(
            selStart,
            cellStartTime,
            newResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        } else {
          updateSelection(
            cellStartTime,
            cellStartTime,
            newResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        }
      }
    },
    [
      viewStartTime,
      allResIds,
      selStart,
      selEnd,
      selResources,
      onSelectionChange,
    ]
  );

  const handleSelectionClick: EDStickyTableCellSelectionClickEvent =
    useCallback(
      ref => {
        if (onSelectionClick) {
          onSelectionClick(ref, selStart, selEnd, selResources);
        }
      },
      [selStart, selEnd, selResources, onSelectionClick]
    );

  useEffect(() => {
    const handleMouseDown = () => setIsDragging(false);
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <EDStickyTable
      width={props.width}
      height={props.height}
      colCount={resCount + 1}
      rowCount={TIME_ROWS + 1}
      minColWidth={MIN_RES_WIDTH}
      firstColWidth={TIME_WIDTH}
      firstColSticky={true}
      minRowHeight={hourHeight}
      firstRowHeight={HEAD_HEIGHT}
      firstRowSticky={true}
      initialScrollToRow={initialScroll}
      cellAllowOverflow={true}
      firstColAllowOverflow={true}
      bottomSpacing={props.bottomSpacing}
    >
      <EDStickyTableHeader>
        <EDStickyTableHeaderStickyCell>
          <EDCalendarViewWeekNumberBox date={viewStartTime} />
        </EDStickyTableHeaderStickyCell>
        {range(0, resCount).map(res => (
          // Titelrad med veckodagar
          <EDStickyTableHeaderCell key={res}>
            <EDCalendarViewResourceNameCell
              resourceName={props.resources[res].name}
            />
          </EDStickyTableHeaderCell>
        ))}
      </EDStickyTableHeader>
      <EDStickyTableRows>
        {range(0, 24).map(hour =>
          range(0, 2).map(halfHour => (
            <EDStickyTableRow key={`h${hour}m${halfHour * 30}`}>
              <EDStickyTableStickyCell>
                <EDCalendarViewClockCell
                  hour={hour}
                  showText={hour > 0 && halfHour === 0}
                />
              </EDStickyTableStickyCell>
              {range(0, resCount).map(res => {
                // Kalenderceller med bokningar
                const key = props.resources[res].id;
                const bookings: IBooking[] = bookingColumns[key];
                const cellStartTime = incMin(
                  incHour(viewStartTime, hour),
                  halfHour * 30
                );
                const cellEndTime = incMin(cellStartTime, 30);
                const selected =
                  props.selStart &&
                  props.selEnd &&
                  timeRangeOverlap(
                    incMin(props.selStart, 15),
                    incMin(props.selEnd, 15),
                    cellStartTime,
                    cellEndTime
                  ) &&
                  selResources?.some(selRes => selRes === key);
                return (
                  <EDStickyTableCell
                    key={res}
                    col={res}
                    row={hour * 2 + halfHour}
                    selected={selected}
                    onCellSelected={handleCellSelected}
                    onSelectionClick={handleSelectionClick}
                    outside={hour < WORK_HOUR_START || hour >= WORK_HOUR_END}
                  >
                    {hour === 0 && halfHour === 0 && bookings
                      ? bookings.map((booking, index) => (
                          <EDCalendarViewTimeViewBooking
                            key={index}
                            booking={booking}
                            startTime={viewStartTime}
                            endTime={viewEndTime}
                            viewportWidth={props.width}
                            viewportHeight={scrollAreaHeight}
                            columnWidth={resWidth}
                            selected={false}
                            onBookingClick={onBookingClick}
                            isDragging={isDragging}
                          />
                        ))
                      : undefined}
                  </EDStickyTableCell>
                );
              })}
            </EDStickyTableRow>
          ))
        )}
      </EDStickyTableRows>
    </EDStickyTable>
  );
};
